import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import type { ScoredAttempt } from '../../types/competition-round';

interface BoulderEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attempts: ScoredAttempt[];
  onRemoveAttempt: (attemptId: string) => void;
}

const ATTEMPT_CONFIG = {
  attempt: { label: 'Attempt', icon: '|', className: 'text-muted-foreground' },
  zone: { label: 'Zone', icon: '+', className: 'text-yellow-600' },
  top: { label: 'Top', icon: 'T', className: 'text-green-600' },
} as const;

export function BoulderEditDialog({
  open,
  onOpenChange,
  attempts,
  onRemoveAttempt,
}: BoulderEditDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {t('judgeBoulder.editAttempts', { defaultValue: 'Edit Attempts' })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
          {attempts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('judgeBoulder.noAttempts', { defaultValue: 'No attempts recorded' })}
            </p>
          ) : (
            attempts.map((attempt, index) => {
              const config = ATTEMPT_CONFIG[attempt.attempt_type];
              return (
                <div
                  key={attempt.id}
                  className="flex items-center justify-between py-2 px-3 border rounded-md"
                  data-testid="attempt-row"
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-mono font-bold text-lg ${config.className}`}>
                      {config.icon}
                    </span>
                    <div>
                      <span className="text-sm font-medium">
                        {t(`judgeBoulder.${attempt.attempt_type}`, { defaultValue: config.label })} {index + 1}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => onRemoveAttempt(attempt.id)}
                    data-testid="remove-attempt-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
